'use client';

import Image from 'next/image';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { TImageMetaPositionAlt, IRetakeReason } from '@northladder/apis';
import { Text } from '@northladder/i18n';
import { convertToHumanReadableTimestamp } from '@northladder/utilities';

import { apiTranslations } from '../../translations';

import { IDeviceImageOptionsWithMetadataTranslations } from '../types';
import { useRouteURL, useTargetAssessmentConfig } from '../hooks';

type TPendingReUploadProps = Pick<
  IDeviceImageOptionsWithMetadataTranslations,
  'title'
> & {
  imagePath: string;
  position: TImageMetaPositionAlt;
  retakeReasons?: IRetakeReason[];
  retakeComment?: string;
  uploadedOn?: number;
};

/**
 * -----------------------------------------------------------------------------
 * Displays that the image has already been uploaded but rejected by the assessment
 * team as unclear or due to some other particular reason.
 *
 * - It will show only the upload status is `RE_UPLOAD`
 * - See also `PendingUploadingV2` & `DoneUploadingV2`
 */
export function PendingReUpload({
  imagePath,
  position,
  retakeReasons,
  retakeComment,
  title,
  uploadedOn,
}: TPendingReUploadProps) {
  const { navigateToRoute } = useRouteURL();
  const { thumbnail, screen } = useTargetAssessmentConfig(position);

  const handleClick = () => {
    navigateToRoute(`/${screen}`, 'push');
  };

  return (
    <button
      className="
        space-between xsm:flex-1 xsm:w-1/2 focus:outline-hidden group relative flex min-h-[400px]
        w-full flex-col items-center overflow-hidden rounded-lg !border-2
        border-4 !border-green-500 border-red-100 !bg-red-500 bg-red-200/20 py-4
        text-center transition-all duration-300
        ease-in hover:border-red-300 hover:bg-red-50 focus:bg-red-50
        focus:ring-1 focus:ring-red-300 focus:ring-offset-2 sm:hover:scale-[1.01]
      "
      onClick={handleClick}
      type="button"
    >
      {imagePath ? (
        <Image
          alt="Image requires a reupload"
          className="absolute z-10 -m-4 w-full object-contain object-center"
          height={1200}
          src={imagePath}
          width={600}
        />
      ) : null}

      <div className="z-20 flex flex-1 flex-col justify-center">
        <Image
          alt="Image requires a reupload"
          className="absolute right-2 top-12 h-1/2 w-1/4 object-contain object-center"
          height={800}
          src={thumbnail}
          width={400}
        />
        <div
          className="
            absolute right-2 top-2 flex items-center justify-center rounded-full
            border-2 border-red-500 bg-red-700 px-2 py-1 text-xs text-white
          "
        >
          <XMarkIcon
            className="
              -ml-1 h-5 w-5 self-center stroke-white stroke-2 ltr:mr-1 rtl:-mr-1
              rtl:ml-1
            "
          />
          <Text text={apiTranslations.reuploadRequired.title} />
        </div>
      </div>
      <div className="broder-red-700 z-20 -mb-4 w-full flex-col border-2 bg-gray-900/40  px-4 py-2 text-sm">
        <p className="flex w-full flex-col items-start pb-1 pt-2 text-start  text-white">
          {title ? (
            <span className="font-bold">
              <Text text={title} />
            </span>
          ) : null}

          <span className="text-xs leading-4">
            <Text text={apiTranslations.reuploadRequired.statement} />
          </span>
          <div className="mb-1 mt-2 w-full">
            {retakeReasons?.map((reasonObj, index) => {
              const reasonsLength = retakeReasons.length;

              return (
                <>
                  <div key={reasonObj._id}>
                    <Text
                      className="mt-1 flex flex-col text-xs leading-3 text-red-100"
                      text={reasonObj.reason}
                    />
                    <Text
                      className="text-[10px] opacity-90"
                      text={reasonObj.description}
                    />
                  </div>
                  {retakeReasons.length >= 2 && index !== reasonsLength - 1 ? (
                    <div className="mt-1 border-b border-gray-300 opacity-10" />
                  ) : null}
                </>
              );
            })}
            {retakeComment ? (
              <p className="text-[10px] opacity-90">{retakeComment}</p>
            ) : null}
          </div>

          <span className="text-primary-50 mt-1 gap-2 text-xs font-light leading-3">
            <Text className="mr-1" text="Uploaded" />
            <Text text={convertToHumanReadableTimestamp(uploadedOn)} />
          </span>
        </p>
      </div>
    </button>
  );
}
